import { useState } from "react";

import { faBolt } from "@fortawesome/pro-regular-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnimatePresence } from "framer-motion";

import { ExternalLink } from "src/base-components/ExternalLink";
import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { DropdownItem } from "src/design-system/Dropdown";
import { Job } from "src/jobs/types";
import {
  DashboardPageParams,
  getBaseUrl,
  getUrlToJobPage,
} from "src/router/urls";
import { pluralize } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const JobsPill: React.FC<{
  jobs?: Job[];
}> = ({ jobs }) => {
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();
  const [isOpen, setOpen] = useState<boolean>(false);

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  if (!jobs)
    return (
      <SkeletonPlaceholder
        dataLoc="jobs-pill-skeleton"
        height="h-5"
        width="w-4/5"
      />
    );

  if (jobs.length === 0) return null;

  return (
    <Menu
      as="div"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <MenuButton as="div" className="cursor-default" onClick={stopPropagation}>
        <Pill variant="outlined-white">
          <Pill.Icon icon={faBolt} />
          <Pill.Text>Used by {pluralize(jobs.length, "Job")}</Pill.Text>
        </Pill>
      </MenuButton>
      <AnimatePresence initial={false}>
        {isOpen && (
          <MenuItems
            anchor={{
              to: "bottom start",
              gap: 6,
              padding: 12,
            }}
            as={MotionDropdownPanel}
            className="min-w-[var(--button-width)]"
            static
          >
            <div className="h-12 px-4 py-2.5 text-left font-inter-semibold-13px">
              This Version is used by
            </div>
            {jobs.map((job) => (
              <MenuItem
                key={job.id}
                as={DropdownItem}
                className="cursor-pointer text-left hover:bg-gray-50"
                onClick={stopPropagation}
              >
                <ExternalLink
                  className="flex items-center gap-x-2 py-2.5 font-inter-normal-13px text-gray-800"
                  href={
                    getBaseUrl() +
                    getUrlToJobPage(orgId, wsId, job.flow_id, job.id)
                  }
                >
                  <Icon color="text-gray-500" icon={faBolt} size="2xs" />
                  {job.name}
                </ExternalLink>
              </MenuItem>
            ))}
          </MenuItems>
        )}
      </AnimatePresence>
    </Menu>
  );
};
